import { useState } from 'react';
import { MessageCircle } from 'lucide-react';
import {
  AssistantPanel,
  type AssistantTransport,
  type AssistantExportToSheet,
  type AssistantError,
} from '@reprotec/assistant-ui';
import { usePermissions } from '../../hooks/usePermissions';
import { api } from '../../lib/api';

/**
 * CB transport for the shared assistant UI (Phase 65-03). Calls the live
 * /assistant/query via api.assistant.query (which carries the __session cookie
 * + unwraps the { data } envelope), and maps the backend's status codes to the
 * typed errors <AssistantPanel/> knows how to display.
 */
/** Map a failed /assistant/query (ApiHttpError carries `status`) to a typed
 *  AssistantError the panel can display. Pure — unit-tested directly. */
export function mapAssistantError(err: unknown): AssistantError {
  const status = (err as { status?: number } | null)?.status;
  if (status === 429) {
    return { kind: 'rate_limited', message: 'Too many requests — please wait a moment.' };
  }
  if (status === 403) {
    return { kind: 'forbidden', message: "You don't have access to the assistant." };
  }
  return { kind: 'unknown', message: 'Something went wrong — please try again.' };
}

export const cbAssistantTransport: AssistantTransport = async ({ message, recentTurns }) => {
  try {
    // api.assistant.query passes the engine's answer through verbatim, including
    // the optional `exportable` descriptor the panel uses to offer a Sheet export.
    return await api.assistant.query({ message, recentTurns });
  } catch (err) {
    throw mapAssistantError(err);
  }
};

/**
 * CB export seam: turns an answer's `exportable` descriptor into a Google Sheet
 * URL via POST /api/v1/assistant/export (carries the __session cookie + unwraps
 * { data }). Maps status codes to typed AssistantErrors so the panel can show a
 * meaningful message on 403/429/503.
 */
export const cbExportToSheet: AssistantExportToSheet = async (exportable) => {
  try {
    return await api.assistant.export(exportable);
  } catch (err) {
    throw mapAssistantError(err);
  }
};

// The 5 target questions seeded as the empty-state prompts (Phase 65 spec).
const EXAMPLE_PROMPTS = [
  'What work is assigned this week?',
  'Are any jobs missing a team lead?',
  'Which jobs have subcontractors on them?',
  'Which jobs have no time booked against them?',
  'Are there any overtime bookings this month?',
];

/**
 * Floating "Ask" launcher + assistant drawer, mounted once in the CB shell.
 * Gated by contracts_board.view (UI gate; the route enforces server-side).
 */
export function AssistantLauncher() {
  const { can } = usePermissions();
  const [open, setOpen] = useState(false);

  if (!can('contracts_board', 'view')) return null;

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Ask the assistant"
          className="fixed bottom-5 right-5 z-40 flex items-center gap-2 rounded-full bg-blue-600 px-4 py-3 text-sm font-medium text-white shadow-lg hover:bg-blue-700"
        >
          <MessageCircle size={18} />
          Ask
        </button>
      )}
      <AssistantPanel
        open={open}
        onClose={() => setOpen(false)}
        transport={cbAssistantTransport}
        exportToSheet={cbExportToSheet}
        examplePrompts={EXAMPLE_PROMPTS}
        title="Reprotec Assistant"
      />
    </>
  );
}
